import React, { useState, useEffect } from "react";
import {
    Row, Col, Card, CardBody, Form, FormGroup, Label, Input,
    Button, Table, InputGroup, InputGroupText, Badge, Modal, ModalHeader, ModalBody
} from "reactstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import CottonCalculator from "../KandiCalculator";
import { FiTruck, FiUser, FiPackage, FiFileText, FiTrash2, FiPlusCircle, FiHash, FiCalendar, FiPlus, } from "react-icons/fi";

// 🔥 MUI DatePicker Imports
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const InvoiceCreate = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Master Data States
    const [clients, setClients] = useState([]);
    const [subClients, setSubClients] = useState([]); // 🔥 Added Sub-Client state
    const [lorries, setLorries] = useState([]);
    const [products, setProducts] = useState([]);

    // Modal States
    const [lorryModal, setLorryModal] = useState(false);
    const [calcModal, setCalcModal] = useState(false);

    const toggleLorryModal = () => setLorryModal(!lorryModal);
    const toggleCalcModal = () => setCalcModal(!calcModal);

    const [formData, setFormData] = useState({
        invoice_no: "",
        invoice_date: dayjs().format('YYYY-MM-DD'),
        client_id: "",
        subclient_id: "", // 🔥 Added subclient_id for API
        lorry_id: "",
        hsn_code: "5201",
        rate_per_kg: "",
        actual_rate_per_kg: 0,
        cgst: 2.5,
        sgst: 2.5,
        items: [{ product_id: "", bags: "", total_kg: "" }]
    });

    const [lorryFormData, setLorryFormData] = useState({
        lorry_number: "",
        driver_name: "",
        driver_phone: ""
    });
    const [lorryErrors, setLorryErrors] = useState({});
    const [errors, setErrors] = useState({});

    // Fetch master data
    const fetchLorries = async () => {
        try {
            const lRes = await Handler({ method: "GET", url: "/lorry/list.php?limit=100&status=1" });
            setLorries(lRes?.data?.map(l => ({ value: l.id, label: `${l.lorry_number} (${l.driver_name})` })) || []);
        } catch (err) { toast.error("Error updating lorry list"); }
    };

    useEffect(() => {
        const fetchData = async () => {
            dispatch(loadingTrue());
            try {
                const [cRes, pRes] = await Promise.all([
                    Handler({ method: "GET", url: "/clients/list.php" }),
                    Handler({ method: "GET", url: "/product/list.php" })
                ]);

                const clientOptions = cRes?.data?.map(c => ({
                    value: c.id,
                    label: c.client?.name?.toUpperCase() || "UNKNOWN"
                })) || [];

                setClients(clientOptions);

                setProducts(pRes?.data?.map(p => ({
                    value: p.id,
                    label: p.name?.toUpperCase()
                })) || []);

                await fetchLorries();
            } catch (err) {
                toast.error("Error loading master data");
            } finally {
                dispatch(loadingFalse());
            }
        };
        fetchData();
    }, [dispatch]);

    // 🔥 Added: Fetch Sub-Clients when Client changes
    useEffect(() => {
        const loadSubClients = async () => {
            if (!formData.client_id) {
                setSubClients([]);
                return;
            }
            try {
                const res = await Handler({ method: "GET", url: `/sub_client/list.php?client_id=${formData.client_id}&status=1` });
                if (res?.success) {
                    setSubClients(res.data.map(sc => ({
                        value: sc.id,
                        label: sc.sub_client?.name?.toUpperCase() || "UNKNOWN"
                    })));
                }
            } catch (err) { console.error("Sub-client fetch error"); }
        };
        loadSubClients();
    }, [formData.client_id]);

    // Lorry Form Handlers
    const handleLorryChange = (e) => {
        const { name, value } = e.target;
        const finalValue = name === "lorry_number" ? value?.toUpperCase().replace(/[^A-Z0-9]/g, '') : value;
        setLorryFormData({ ...lorryFormData, [name]: finalValue });
        if (lorryErrors[name]) setLorryErrors({ ...lorryErrors, [name]: "" });
    };

    const validateLorryForm = () => {
        let fErrors = {};
        let isValid = true;
        if (!lorryFormData.lorry_number.trim()) { fErrors.lorry_number = "Lorry number is required"; isValid = false; }
        if (!lorryFormData.driver_name.trim()) { fErrors.driver_name = "Driver name is required"; isValid = false; }
        if (!lorryFormData.driver_phone) { fErrors.driver_phone = "Phone is required"; isValid = false; }
        else if (!/^[0-9]{10}$/.test(lorryFormData.driver_phone)) { fErrors.driver_phone = "Invalid 10-digit number"; isValid = false; }
        setLorryErrors(fErrors);
        return isValid;
    };

    const handleLorrySubmit = async (e) => {
        e.preventDefault();
        if (!validateLorryForm()) { toast.error("Please fill lorry details correctly!"); return; }
        dispatch(loadingTrue());
        try {
            const res = await Handler({ method: "POST", url: "/lorry/create.php", data: lorryFormData });
            if (res?.success) {
                Swal.fire("Success!", "Lorry registered successfully", "success");
                await fetchLorries();
                setLorryFormData({ lorry_number: "", driver_name: "", driver_phone: "" });
                toggleLorryModal();
            } else { toast.error(res?.message || "Failed to register lorry"); }
        } catch (err) { toast.error("Server error occurred"); }
        finally { dispatch(loadingFalse()); }
    };

    // Invoice Item Logic
    const addItem = () => {
        setFormData({ ...formData, items: [...formData.items, { product_id: "", bags: "", total_kg: "" }] });
    };

    const removeItem = (index) => {
        const newItems = formData.items.filter((_, i) => i !== index);
        setFormData({ ...formData, items: newItems });
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...formData.items];
        newItems[index][field] = value;
        if (field === "product_id") {
            const selectedProd = products.find(p => p.value === value);
            let hsn = selectedProd?.label.toLowerCase().includes("seed") ? "1207" : "5201";
            setFormData({ ...formData, items: newItems, hsn_code: hsn });
        } else {
            setFormData({ ...formData, items: newItems });
        }
    };

    const calculateSummary = () => {
        let totalKg = formData.items.reduce((acc, item) => acc + Number(item.total_kg || 0), 0);
        const subtotal = totalKg * Number(formData.rate_per_kg || 0);
        const actualTotal = totalKg * Number(formData.actual_rate_per_kg || 0);
        const cgstAmt = (subtotal * 2.5) / 100;
        const sgstAmt = (subtotal * 2.5) / 100;
        return {
            totalKg: Math.round(totalKg),
            subtotal: Math.round(subtotal),
            cgstAmt: Math.round(cgstAmt),
            sgstAmt: Math.round(sgstAmt),
            grandTotal: Math.round(subtotal + cgstAmt + sgstAmt),
            profit: Math.round(subtotal - actualTotal)
        };
    };

    const summary = calculateSummary();

    const validateForm = () => {
        let isValid = true;
        if (!formData.invoice_no || !formData.client_id || !formData.lorry_id || !formData.rate_per_kg) isValid = false;
        formData.items.forEach(item => { if (!item.product_id || !item.total_kg) isValid = false; });
        if (!isValid) toast.error("Please fill all required fields correctly!");
        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        const result = await Swal.fire({
            title: 'Confirm Invoice?',
            text: "Final net amount: ₹" + summary.grandTotal.toLocaleString(),
            icon: 'question', showCancelButton: true, confirmButtonText: 'Generate & Print', confirmButtonColor: '#2e4a3d'
        });

        if (result.isConfirmed) {
            dispatch(loadingTrue());
            try {
                const finalPayload = {
                    ...formData,
                    rate_per_kg: parseFloat(formData.rate_per_kg),
                    actual_rate_per_kg: formData.actual_rate_per_kg ? parseFloat(formData.actual_rate_per_kg) : 0,
                    total_kg: summary.totalKg, sub_total: summary.subtotal, grand_total: summary.grandTotal
                };
                const res = await Handler({ method: "POST", url: "/invoice/create.php", data: finalPayload });
                if (res?.success) {
                    await Swal.fire("Success!", "Invoice Generated", "success");
                    navigate("/invoices");
                } else { toast.error(res?.message || "Failed to create invoice"); }
            } catch (err) { toast.error("Failed to create invoice"); }
            finally { dispatch(loadingFalse()); }
        }
    };

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided, minHeight: '48px', borderRadius: '10px',
            borderColor: state.isFocused ? '#2e4a3d' : '#dee2e6', boxShadow: 'none', backgroundColor: '#f8f9fa'
        }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    const blockInvalidChar = (e) => ["-", "e", "E"].includes(e.key) && e.preventDefault();

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-3 p-md-4 bg-light min-vh-100">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h3 className="fw-bold text-dark m-0">Create Dispatch Invoice</h3>
                        <p className="text-muted small m-0">Generate outbound bills for clients.</p>
                    </div>
                    <div className="d-flex gap-2">
                        <Button color="primary" outline className="rounded-pill px-3 shadow-sm" onClick={toggleCalcModal}>
                            Kandi Calc
                        </Button>
                        <Badge color="success" className="p-2 px-3 rounded-pill shadow-sm d-flex align-items-center">Drafting Mode</Badge>
                    </div>
                </div>

                <Form onSubmit={handleSubmit}>
                    <Row className="g-4">
                        <Col lg={8}>
                            <Card className="border-0 shadow-sm rounded-4 mb-4">
                                <CardBody className="p-4">
                                    <h6 className="fw-bold mb-4 text-uppercase ls-1"><FiFileText className="me-2" /> Invoice Details</h6>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label className="small fw-bold text-uppercase">Invoice No *</Label>
                                                <InputGroup className="bg-light rounded-3 border">
                                                    <InputGroupText className="bg-transparent border-0"><FiHash /></InputGroupText>
                                                    <Input type="text" placeholder="Enter No" className="bg-transparent border-0 fw-bold" style={{ height: '48px' }} value={formData.invoice_no} onChange={(e) => setFormData({ ...formData, invoice_no: e.target.value })} />
                                                </InputGroup>
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup className="d-flex flex-column">
                                                <Label className="small fw-bold text-uppercase">Invoice Date *</Label>
                                                <DatePicker
                                                    disableFuture format="DD/MM/YYYY" value={dayjs(formData.invoice_date)}
                                                    onChange={(v) => setFormData({ ...formData, invoice_date: v ? v.format('YYYY-MM-DD') : '' })}
                                                    slotProps={{ textField: { fullWidth: true, variant: "outlined", sx: { '& .MuiOutlinedInput-root': { backgroundColor: '#f8f9fa', borderRadius: '10px', height: '48px', '& fieldset': { border: '1px solid #dee2e6' } } } } }}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>

                            <Card className="border-0 shadow-sm rounded-4 mb-4">
                                <CardBody className="p-4">
                                    <h6 className="fw-bold mb-4 text-uppercase ls-1"><FiTruck className="me-2" /> Logistics & Client</h6>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label className="small fw-bold">CLIENT NAME *</Label>
                                                <Select styles={customSelectStyles} options={clients} onChange={(opt) => setFormData({ ...formData, client_id: opt.value, subclient_id: "" })} placeholder="Select Party..." />
                                            </FormGroup>
                                        </Col>
                                        {/* 🔥 Added: Sub-Client Select Box */}
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label className="small fw-bold">SUB CLIENT (Optional) for other parties</Label>
                                                <Select 
                                                    styles={customSelectStyles} 
                                                    options={subClients} 
                                                    isDisabled={!formData.client_id}
                                                    value={subClients.find(s => s.value === formData.subclient_id) || null}
                                                    onChange={(opt) => setFormData({ ...formData, subclient_id: opt ? opt.value : "" })} 
                                                    placeholder={formData.client_id ? "Select Sub Client..." : "Select Client First"}
                                                    isClearable
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={12}>
                                            <FormGroup>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <Label className="small fw-bold">VEHICLE / LORRY *</Label>
                                                    <Button color="link" className="p-0 text-primary small fw-bold text-decoration-none" onClick={toggleLorryModal}>
                                                        <FiPlus /> Add New
                                                    </Button>
                                                </div>
                                                <Select styles={customSelectStyles} options={lorries} value={lorries.find(l => l.value === formData.lorry_id)} onChange={(opt) => setFormData({ ...formData, lorry_id: opt.value })} placeholder="Select Vehicle..." />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>

                            <Card className="border-0 shadow-sm rounded-4">
                                <CardBody className="p-4">
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <h6 className="fw-bold m-0 text-uppercase ls-1"><FiPackage className="me-2" /> Dispatch Items</h6>
                                        <Button color="primary" size="sm" outline onClick={addItem} className="rounded-pill px-3 border-2">
                                            <FiPlusCircle className="me-1" /> Add Item
                                        </Button>
                                    </div>
                                    <Table className="align-middle border-0">
                                        <thead className="bg-light small text-uppercase text-muted">
                                            <tr>
                                                <th className="border-0">Product</th>
                                                <th className="border-0 text-center" style={{ width: '20%' }}>Bags</th>
                                                <th className="border-0 text-center" style={{ width: '25%' }}>Weight (KG)</th>
                                                <th className="border-0" style={{ width: '50px' }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.items.map((item, index) => (
                                                <tr key={index}>
                                                    <td className="ps-0 border-0">
                                                        <Select styles={customSelectStyles} options={products} onChange={(opt) => handleItemChange(index, "product_id", opt.value)} />
                                                    </td>
                                                    <td className="border-0">
                                                        <Input type="number" min={0} onKeyDown={blockInvalidChar}  value={item.bags} onChange={(e) => handleItemChange(index, "bags", e.target.value)} className="bg-light border-0 text-center" style={{ height: '48px', borderRadius: '10px' }} />
                                                    </td>
                                                    <td className="border-0">
                                                        <Input type="number" min={0} onKeyDown={blockInvalidChar} step="0.01" value={item.total_kg} onChange={(e) => handleItemChange(index, "total_kg", e.target.value)} className="bg-light border-0 text-center" style={{ height: '48px', borderRadius: '10px' }} />
                                                    </td>
                                                    <td className="border-0 text-end pe-0">
                                                        {formData.items.length > 1 && <Button color="link" className="text-danger p-0" onClick={() => removeItem(index)}><FiTrash2 size={18} /></Button>}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </CardBody>
                            </Card>
                        </Col>

                        <Col lg={4}>
                            <Card className="border-0 shadow-sm rounded-4 sticky-top" style={{ top: '100px' }}>
                                <CardBody className="p-4">
                                    <h6 className="fw-bold mb-4 text-uppercase ls-1">Invoice Summary</h6>
                                    <FormGroup className="mb-3">
                                        <Label className="small fw-bold text-success text-uppercase">Selling Rate (per KG) *</Label>
                                        <InputGroup className="bg-light rounded-3 border">
                                            <InputGroupText className="bg-transparent border-0 text-success fw-bold">₹</InputGroupText>
                                            <Input className="bg-transparent border-0 fw-bold text-success" min={0} type="number" step="0.01" value={formData.rate_per_kg} onChange={(e) => setFormData({ ...formData, rate_per_kg: e.target.value })} />
                                        </InputGroup>
                                    </FormGroup>
                                    <FormGroup className="mb-4">
                                        <Label className="small fw-bold text-muted text-uppercase">Actual Rate (Profit Check)</Label>
                                        <InputGroup className="bg-light rounded-3 border">
                                            <InputGroupText className="bg-transparent border-0 text-muted fw-bold">₹</InputGroupText>
                                            <Input className="bg-transparent border-0 fw-bold" min={0} type="number" step="0.01" value={formData.actual_rate_per_kg} onChange={(e) => setFormData({ ...formData, actual_rate_per_kg: e.target.value })} />
                                        </InputGroup>
                                    </FormGroup>
                                    <div className="bg-light p-3 rounded-4 mb-4 border">
                                        <div className="d-flex justify-content-between mb-2 small text-muted"><span>Total Weight:</span><span className="fw-bold text-dark">{summary.totalKg?.toFixed(2)} KG</span></div>
                                        {formData.actual_rate_per_kg > 0 && (
                                            <div className="d-flex justify-content-between mb-2 small text-muted"><span>Adjusted Profit:</span><span className={`fw-bold ${summary.profit >= 0 ? 'text-success' : 'text-danger'}`}>₹ {summary.profit.toLocaleString()}</span></div>
                                        )}
                                        <div className="d-flex justify-content-between pt-2 border-top"><span className="fw-bold">Taxable Amt:</span><span className="fw-bold">₹ {summary.subtotal.toLocaleString()}</span></div>
                                    </div>
                                    <div className="bg-primary text-white p-4 rounded-4 text-center mb-4 shadow-sm">
                                        <p className="small opacity-75 mb-1 text-uppercase fw-bold">Grand Total (5%)</p>
                                        <h2 className="fw-bold m-0">₹ {summary.grandTotal.toLocaleString()}</h2>
                                    </div>
                                    <Button color="primary" block className="py-3 fw-bold shadow-sm rounded-3 border-0" type="submit">GENERATE DISPATCH INVOICE</Button>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Form>

                {/* Modal: Add Lorry */}
                <Modal isOpen={lorryModal} toggle={toggleLorryModal} centered>
                    <ModalHeader toggle={toggleLorryModal} className="border-bottom-0 pb-0">Register New Lorry</ModalHeader>
                    <ModalBody className="p-4">
                        <Form onSubmit={handleLorrySubmit}>
                            <FormGroup className="mb-3">
                                <Label className="fw-bold">Lorry Number *</Label>
                                <InputGroup className={lorryErrors.lorry_number ? "is-invalid border-danger" : ""}>
                                    <InputGroupText className="bg-light"><FiTruck /></InputGroupText>
                                    <Input type="text" name="lorry_number" placeholder="TN33AA1234" value={lorryFormData.lorry_number} onChange={handleLorryChange} />
                                </InputGroup>
                                {lorryErrors.lorry_number && <small className="text-danger">{lorryErrors.lorry_number}</small>}
                            </FormGroup>
                            <Row>
                                <Col md={6}>
                                    <FormGroup className="mb-3">
                                        <Label className="fw-bold">Driver Name *</Label>
                                        <Input type="text" name="driver_name" placeholder="Name" value={lorryFormData.driver_name} onChange={handleLorryChange} className="text-capitalize" />
                                        {lorryErrors.driver_name && <small className="text-danger">{lorryErrors.driver_name}</small>}
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup className="mb-3">
                                        <Label className="fw-bold">Phone Number *</Label>
                                        <Input type="text" name="driver_phone" placeholder="10 Digit" maxLength="10" value={lorryFormData.driver_phone} onChange={handleLorryChange} />
                                        {lorryErrors.driver_phone && <small className="text-danger">{lorryErrors.driver_phone}</small>}
                                    </FormGroup>
                                </Col>
                            </Row>
                            <div className="mt-3 d-flex gap-2">
                                <Button type="submit" color="primary" className="flex-grow-1">Register Lorry</Button>
                                <Button color="light" onClick={toggleLorryModal}>Cancel</Button>
                            </div>
                        </Form>
                    </ModalBody>
                </Modal>

                {/* Modal: Cotton Calculator */}
                <Modal isOpen={calcModal} toggle={toggleCalcModal} size="lg" centered>
                    <ModalHeader toggle={toggleCalcModal} className="border-bottom-0 pb-0">Cotton Kandi Calculator</ModalHeader>
                    <ModalBody className="p-4">
                        <CottonCalculator />
                        <div className="text-end mt-3">
                            <Button color="secondary" outline onClick={toggleCalcModal}>Close</Button>
                        </div>
                    </ModalBody>
                </Modal>
            </div>
        </LocalizationProvider>
    );
};

export default InvoiceCreate;